export const GATED_SESSION_CACHE_PREFIX = "ranks_gated_v3__";
export const GATED_ENVELOPE_VERSION = "3";

// Gated engagement is stored inside the consolidated per-type payload. Keep
// accepting the legacy top-level shape so an already-populated browser cache
// can be read while it expires naturally.
export function normalizeEngagementItems(payload) {
  const items = payload?.engagement?.items ?? payload?.items;
  if (!Array.isArray(items)) throw new Error("Invalid engagement payload");
  return items;
}

const SEARCH_FIELD_KEYS = ["searchRank", "searchChange", "searchPrice", "searchName", "searchAuthor"];

export function normalizeGatedSearchFields(searchFields = {}) {
  return SEARCH_FIELD_KEYS.reduce((fields, key) => {
    fields[key] = searchFields[key] !== false;
    return fields;
  }, {});
}

export function buildGatedContextKey(context = {}) {
  const search = String(context.search || "")
    .trim()
    .toLowerCase();
  const category = context.category || "_";
  const searchFields = normalizeGatedSearchFields(context.searchFields);
  const fieldKey = SEARCH_FIELD_KEYS.map((key) => (searchFields[key] ? "1" : "0")).join("");
  return `${context.type}|${context.tab}|${category}|${search || "_"}|${fieldKey}`;
}

export function getGatedSessionStorageKey(contextKey) {
  return `${GATED_SESSION_CACHE_PREFIX}${contextKey}`;
}

export function isCategoryChurnMetricsValid(categoryChurn) {
  if (!categoryChurn || typeof categoryChurn !== "object") return false;
  const numberFields = [
    "movedUpCount",
    "totalRankedCount",
    "movedUpPercent",
    "totalRankGain",
    "topItemRankGain",
    "topFiveRankGain",
  ];
  for (const field of numberFields) {
    if (typeof categoryChurn[field] !== "number" || Number.isNaN(categoryChurn[field])) return false;
  }
  const nullableNumberFields = ["medianRankGain", "avgRankGain", "topItemRankGainShare", "topFiveRankGainShare"];
  for (const field of nullableNumberFields) {
    const value = categoryChurn[field];
    if (value !== null && (typeof value !== "number" || Number.isNaN(value))) return false;
  }
  return true;
}

export function isGatedCacheEnvelopeValid(envelope, expectedContextKey) {
  if (!envelope || typeof envelope !== "object") return false;
  if (typeof envelope.schemaVersion !== "string" || envelope.schemaVersion !== GATED_ENVELOPE_VERSION) return false;
  if (typeof envelope.contextKey !== "string" || envelope.contextKey !== expectedContextKey) return false;
  if (typeof envelope.lastUpdated !== "string" || !envelope.lastUpdated) return false;
  if (typeof envelope.positiveSum !== "number") return false;
  if (typeof envelope.negativeSum !== "number") return false;
  if (!isCategoryChurnMetricsValid(envelope.categoryChurn)) return false;
  return true;
}

function formatCompactNumber(value, options = {}) {
  if (value === null || value === undefined) return "-";
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toLocaleString(undefined, options);
}

function formatRankGainValue(value) {
  if (value === null || value === undefined) return "-";
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return Math.round(value).toLocaleString();
}

export function formatCompactRankGainDisplay(value) {
  if (value === null || value === undefined) return "-";
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  const roundedValue = Math.round(value);
  if (Math.abs(roundedValue) <= 999) return roundedValue.toLocaleString();
  const absoluteValue = Math.abs(roundedValue);
  const divisor = absoluteValue >= 1_000_000 ? 1_000_000 : 1_000;
  const suffix = absoluteValue >= 1_000_000 ? "M" : "K";
  const truncatedTenth = Math.trunc((absoluteValue / divisor) * 10) / 10;
  const formatted =
    truncatedTenth % 1 === 0
      ? truncatedTenth.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : truncatedTenth.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${roundedValue < 0 ? "-" : ""}${formatted}${suffix}`;
}

function formatRankGainDeltaHtml(value) {
  if (value === null || value === undefined || typeof value !== "number" || Number.isNaN(value)) return "-";
  const numericValue = Math.round(value);
  if (numericValue === 0) return "0";
  const direction = numericValue > 0 ? "up" : "down";
  const colorClass = numericValue > 0 ? "text-green-600" : "text-red-600";
  const formatted = formatCompactRankGainDisplay(Math.abs(numericValue));
  return `<span class="churn-metrics__delta ${colorClass}"><span class="delta-marker delta-${direction}" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M5 1 L9 8 L1 8 Z" fill="currentColor"/></svg></span>${formatted}</span>`;
}

export function formatRelativePercentDelta(current, previous) {
  if (
    typeof current !== "number" ||
    !Number.isFinite(current) ||
    typeof previous !== "number" ||
    !Number.isFinite(previous)
  ) {
    return "";
  }
  if (previous === 0) return current === 0 ? "0%" : "new";
  const percent = Math.round(((current - previous) / Math.abs(previous)) * 100);
  return `${percent > 0 ? "+" : ""}${percent}%`;
}

function formatMetricDeltaHtml(current, previous) {
  const text = formatRelativePercentDelta(current, previous);
  if (!text) return "";
  const numeric = text.endsWith("%") ? Number.parseInt(text, 10) : 1;
  const tone = numeric > 0 ? "text-green-600" : numeric < 0 ? "text-red-600" : "";
  return `<span class="churn-metrics__relative-delta ${tone}">(${text})</span>`;
}

function formatAverageRankGain(value) {
  if (value === null || value === undefined) return "-";
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return Math.round(value).toLocaleString();
}

function formatPercentValue(value) {
  if (value === null || value === undefined) return "-";
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  const roundedToOne = Math.round(value * 10) / 10;
  const fractionDigits = Number.isInteger(roundedToOne) ? 0 : 1;
  return `${roundedToOne.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`;
}

function formatRatioValue(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  return `${value.toFixed(1)}×`;
}

function isEmptyCategoryChurn(categoryChurn) {
  return Number(categoryChurn?.totalRankedCount || 0) <= 0;
}

export function formatCategoryChurnMetrics(categoryChurn) {
  if (!isCategoryChurnMetricsValid(categoryChurn)) return "";
  if (isEmptyCategoryChurn(categoryChurn)) {
    return [
      "Moved up: - (-)",
      "Median rank gain: -",
      "Avg rank gain: -",
      "Total rank gain: -",
      "Top item rank gain: - (-)",
      "Top 5 rank gain: - (-)",
    ].join(" | ");
  }
  return [
    `Moved up: ${formatCompactNumber(categoryChurn.movedUpCount)} / ${formatCompactNumber(categoryChurn.totalRankedCount)} (${formatPercentValue(categoryChurn.movedUpPercent)})`,
    `Median rank gain: ${formatRankGainValue(categoryChurn.medianRankGain)}`,
    `Avg rank gain: ${formatAverageRankGain(categoryChurn.avgRankGain)}`,
    `Total rank gain: ${formatCompactNumber(categoryChurn.totalRankGain)}`,
    `Top item rank gain: ${formatCompactNumber(categoryChurn.topItemRankGain)} (${formatPercentValue(categoryChurn.topItemRankGainShare)})`,
    `Top 5 rank gain: ${formatCompactNumber(categoryChurn.topFiveRankGain)} (${formatPercentValue(categoryChurn.topFiveRankGainShare)})`,
  ].join(" | ");
}

function churnLabel(text) {
  return `<span class="churn-metrics__label">${text}</span>`;
}

function churnValue(text, modifier = "", tone = "") {
  const modifierClass = modifier ? ` churn-metrics__value--${modifier}` : "";
  const toneClass = tone ? ` churn-metrics__value--${tone}` : "";
  return `<span class="churn-metrics__value${modifierClass}${toneClass}">${text}</span>`;
}

function churnValueWithPercent(valueText, percentText, modifier = "", tone = "") {
  return churnValue(`${valueText} (${percentText})`, modifier, tone);
}

function churnSeparator(text, modifier = "") {
  const modifierClass = modifier ? ` churn-metrics__separator--${modifier}` : "";
  return `<span class="churn-metrics__separator${modifierClass}">${text}</span>`;
}

function churnTooltip(lines) {
  const tooltipLines = (Array.isArray(lines) ? lines : [lines]).map(
    (line) => `<span class="churn-metrics__tooltip-line">${line}</span>`,
  );
  return `<div class="tooltip top"><div class="tooltip-item">${tooltipLines.join("")}</div></div>`;
}

function churnMetric(key, label, values, tooltipText, attributes = "") {
  const actionAttributes = attributes ? ` role="button" tabindex="0" ${attributes}` : "";
  return `<span class="churn-metrics__metric churn-metrics__metric--${key} with-tooltip"${actionAttributes}>${churnLabel(label)}<span class="churn-metrics__metric-values">${values.join("")}</span>${churnTooltip(tooltipText)}</span>`;
}

export function formatCategoryChurnMetricsHtml(categoryChurn, previousCategoryChurn = null, upDownRatio) {
  if (!isCategoryChurnMetricsValid(categoryChurn)) return "";
  const hasRankedItems = !isEmptyCategoryChurn(categoryChurn);
  const movedUpCount = hasRankedItems ? formatCompactNumber(categoryChurn.movedUpCount) : "-";
  const movedUpPercent = hasRankedItems ? formatPercentValue(categoryChurn.movedUpPercent) : "-";
  const totalRankGain = hasRankedItems ? formatRankGainDeltaHtml(categoryChurn.totalRankGain) : "-";
  const topItemRankGain = hasRankedItems ? formatRankGainDeltaHtml(categoryChurn.topItemRankGain) : "-";
  const topItemRankGainShare = hasRankedItems ? formatPercentValue(categoryChurn.topItemRankGainShare) : "-";
  const topFiveRankGain = hasRankedItems ? formatRankGainDeltaHtml(categoryChurn.topFiveRankGain) : "-";
  const topFiveRankGainShare = hasRankedItems ? formatPercentValue(categoryChurn.topFiveRankGainShare) : "-";
  const avgRankGainHtml = hasRankedItems ? formatRankGainDeltaHtml(categoryChurn.avgRankGain) : "-";
  const medianRankGainHtml = hasRankedItems ? formatRankGainDeltaHtml(categoryChurn.medianRankGain) : "-";
  const previous =
    previousCategoryChurn && isCategoryChurnMetricsValid(previousCategoryChurn) ? previousCategoryChurn : null;
  const avgMidSeparator = hasRankedItems ? " / " : "/";
  const movedUpTooltip = hasRankedItems
    ? [
        `${movedUpCount} items moved up,`,
        `${movedUpPercent} out of ${formatCompactNumber(categoryChurn.totalRankedCount)} total`,
      ]
    : ["No ranked items found", "for this view"];
  const totalRankGainTooltip = ["Sum of all rank gains", "in the list"];
  const topItemTooltip = hasRankedItems
    ? [`Top 1 item jumped ${topItemRankGain} spots today,`, `${topItemRankGainShare} of all rank gains in the list`]
    : ["Top 1 item jumped - spots today,", "- of all rank gains in the list"];
  const topFiveTooltip = hasRankedItems
    ? [
        `Top 5 items combined gained ${topFiveRankGain} positions,`,
        `${topFiveRankGainShare} of all rank gains in the list`,
      ]
    : ["Top 5 items combined gained - positions,", "- of all rank gains in the list"];
  const avgMidTooltip = hasRankedItems
    ? [`On average items moved up by ${avgRankGainHtml} spots,`, `median is ${medianRankGainHtml}`]
    : ["On average items moved up by - spots,", "median is -"];
  const metrics = [
    churnMetric(
      "moved-up",
      "Items Up",
      [
        churnValueWithPercent(movedUpCount, movedUpPercent, "count"),
        formatMetricDeltaHtml(categoryChurn.movedUpCount, previous?.movedUpCount),
      ],
      movedUpTooltip,
      'data-search-command="delta:>0"',
    ),
    churnMetric(
      "total-rank-gain",
      "Up Sum",
      [
        churnValue(totalRankGain, "rank-gain", hasRankedItems ? "positive" : ""),
        formatMetricDeltaHtml(categoryChurn.totalRankGain, previous?.totalRankGain),
      ],
      totalRankGainTooltip,
      'data-search-command="delta:>0"',
    ),
    churnMetric(
      "top-item-rank-gain",
      "Top 1 Up",
      [
        churnValueWithPercent(topItemRankGain, topItemRankGainShare, "rank-gain", hasRankedItems ? "positive" : ""),
        formatMetricDeltaHtml(categoryChurn.topItemRankGain, previous?.topItemRankGain),
      ],
      topItemTooltip,
      'data-search-command="delta:>0" data-rank-highlight-count="1"',
    ),
    churnMetric(
      "top-five-rank-gain",
      "Top 5 Up Sum",
      [
        churnValueWithPercent(topFiveRankGain, topFiveRankGainShare, "rank-gain", hasRankedItems ? "positive" : ""),
        formatMetricDeltaHtml(categoryChurn.topFiveRankGain, previous?.topFiveRankGain),
      ],
      topFiveTooltip,
      'data-search-command="delta:>0" data-rank-highlight-count="5"',
    ),
    churnMetric(
      "avg-mid-rank-gain",
      "Avg/Mid",
      [
        churnValue(avgRankGainHtml, "rank-gain", "positive"),
        churnSeparator(avgMidSeparator, "fraction"),
        churnValue(medianRankGainHtml, "rank-gain", "positive"),
        formatMetricDeltaHtml(categoryChurn.avgRankGain, previous?.avgRankGain),
        formatMetricDeltaHtml(categoryChurn.medianRankGain, previous?.medianRankGain),
      ],
      avgMidTooltip,
    ),
  ];
  if (upDownRatio !== undefined) {
    metrics.push(
      churnMetric(
        "up-down-ratio",
        "Up/Down",
        [churnValue(formatRatioValue(upDownRatio), "ratio", hasRankedItems ? "positive" : "")],
        ["Up moves divided by down moves", "higher means more upward movement"],
      ),
    );
  }
  return metrics.join('<span class="churn-metrics__metric-sep" aria-hidden="true">·</span>');
}

export function buildGatedEnvelope(contextKey, metrics) {
  if (!metrics?.lastUpdated || !isCategoryChurnMetricsValid(metrics?.categoryChurn)) return null;
  if (typeof metrics.positiveSum !== "number" || typeof metrics.negativeSum !== "number") return null;
  return {
    schemaVersion: GATED_ENVELOPE_VERSION,
    contextKey,
    lastUpdated: metrics.lastUpdated,
    positiveSum: metrics.positiveSum,
    negativeSum: metrics.negativeSum,
    categoryChurn: metrics.categoryChurn,
  };
}

export function normalizeRenderableGatedMetrics(metrics) {
  if (!metrics || typeof metrics.positiveSum !== "number" || typeof metrics.negativeSum !== "number") return null;
  if (!isCategoryChurnMetricsValid(metrics.categoryChurn)) return null;
  return metrics;
}
